// My past visits on the engineer PWA (Session ASB, Director decision D-SEC-6).
//
// Why this exists: D-SEC-6 takes a site off the engineer's site list the moment their open work there
// is finished, but lets them keep their OWN past jobs for good. The server has always served those
// records (GET /api/engineer/job-sheets/:jobId answers for the engineer's own job at any site), yet the
// only links to a record were the site and lift history screens, and those close with the site. So on a
// site whose last job was theirs, an engineer could never open their own completed visit again. This
// list is the way back: their own completed visits, newest first, each opening the same record screen
// the history feeds open.
//
// Read-only. Live whenever there is signal, and saved for when there is none (Session ASB fix review): each
// page is read from the server through the history cache (loadMyVisits, src/lib/offline/historyStore.ts) and
// saved on the phone, so with no signal the list last seen is still shown, under the usual "Showing saved
// information" notice, and still links to each record. So is a request that is still unanswered after about
// eight seconds (MY_VISITS_TIMEOUT_MS, the review of fix round 2): one bar of signal must not hold the saved
// list behind "Loading...". A page that was never saved waits for the answer however slow it is, since giving
// up there would only turn a slow answer into a failure (fix round 4); if it fails outright, the page says so
// and offers Try again. The site-list prune keeps this list and the engineer's own records whatever their
// site, so a visit at a site that has left the site list stays reachable offline; a record never opened on
// this phone still needs signal to open. Sign-out clears both. When a session merely runs out, reading this
// list clears nothing (readThroughCache clears only for a refusal that takes access away): whether the copies
// stay is the auth layer's decision (auth.tsx).
//
// A PERSONAL surface (the Session AGV rule, src/lib/pwaIdentity.ts): it belongs to an Engineer row. An
// office account driving the PWA with no engineer of its own is told so and pointed at the office's
// Service records page, rather than shown an empty list that reads as "you have done nothing".
import { StatusPill } from "../../components/StatusPill";
import { statePill } from "../../lib/statusRegistry";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { EngineerHistoryJob } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { loadMyVisits } from "../../lib/offline/historyStore";
import JobTypeBadge from "../../components/JobTypeBadge";
import { hasOwnEngineerRecords, isOversightAccount } from "../../lib/pwaIdentity";
import { OversightNotice } from "./OversightNotice";

type MyVisit = EngineerHistoryJob & { siteName: string | null };

const HERE = "/engineer/history/mine";

// Append a page, dropping any visit already on screen (a visit approved between two page fetches
// shifts the offset boundary and can repeat a row).
function mergeVisits(prev: MyVisit[], next: MyVisit[]): MyVisit[] {
  const seen = new Set(prev.map((v) => v.id));
  return [...prev, ...next.filter((v) => !seen.has(v.id))];
}

function dateLabel(iso: string | null): string {
  if (!iso) return "Date not recorded";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "Date not recorded"
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
}

function VisitRow({ visit }: { visit: MyVisit }) {
  const where = [visit.siteName, visit.eleNumber].filter(Boolean).join(" | ");
  return (
    <li>
      <Link
        to={`/engineer/history/visits/${visit.id}`}
        state={{ from: HERE }}
        className="block rounded-ele border border-ele-purple-accent bg-ele-surface p-3"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-heading text-sm text-ele-strong">{dateLabel(visit.completedAt)}</p>
            <p className="mt-0.5 text-xs text-ele-body">{where || "No lift recorded"}</p>
          </div>
          <JobTypeBadge type={visit.type} size="sm" />
        </div>
        <p className="mt-2 text-sm text-ele-body">{visit.outcome}</p>
        {/* Session SC9A: the one state this list shows, in the same look as the office (statusRegistry.ts). */}
        {visit.followOnWorks === true && <StatusPill state={statePill("follow_on")} className="mt-2" />}
      </Link>
    </li>
  );
}

export function MyPastVisitsPage() {
  const { user } = useAuth();
  const ownRecords = hasOwnEngineerRecords(user);
  const [visits, setVisits] = useState<MyVisit[] | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(false);
  const [stale, setStale] = useState(false);

  const load = useCallback(async (next: number) => {
    setBusy(true);
    setError(false);
    try {
      const res = await loadMyVisits(next);
      setVisits((prev) => (next === 1 || !prev ? res.data.items : mergeVisits(prev, res.data.items)));
      setHasMore(res.data.hasMore);
      setPage(next);
      // Labelled as saved information while any page on screen came from the phone rather than the server.
      setStale((prev) => (next === 1 ? res.stale : prev || res.stale));
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (ownRecords) void load(1);
  }, [load, ownRecords]);

  if (!ownRecords) {
    return (
      <OversightNotice
        title="My past visits"
        explanation={
          isOversightAccount(user)
            ? "You are signed in with an office account, which has no visits of its own. Every completed visit is on the office Service records page."
            : "This account has no engineer profile, so it has no visits of its own."
        }
        officeHref={isOversightAccount(user) ? "/office/jobsheets" : undefined}
        officeLabel={isOversightAccount(user) ? "Open office service records" : undefined}
      />
    );
  }

  return (
    <div>
      <Link to="/engineer" className="mb-3 inline-flex min-h-11 items-center font-menu text-xs tracking-wider text-ele-body">
        Back to home
      </Link>
      <h1 className="mb-1 font-heading text-2xl tracking-wide text-ele-strong">My past visits</h1>
      <p className="mb-4 text-sm text-ele-body">
        Your own completed visits, newest first. They stay here after a site has left your site list.
      </p>
      {stale && visits && (
        <p className="mb-3 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-xs text-ele-body">
          Showing saved information. It will update when you are back online.
        </p>
      )}

      {error && !visits ? (
        <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-body">
          <p role="alert">Could not load your past visits just now. They will appear when your signal improves.</p>
          <button
            type="button"
            onClick={() => void load(1)}
            disabled={busy}
            className="mt-3 inline-flex min-h-11 items-center rounded-ele border border-ele-field-border px-4 font-menu text-xs tracking-wider text-ele-body disabled:opacity-50"
          >
            Try again
          </button>
        </div>
      ) : !visits ? (
        <p className="text-sm text-ele-body">Loading...</p>
      ) : visits.length === 0 ? (
        <p className="text-sm text-ele-body">No completed visits yet. A visit appears here once the office has approved it.</p>
      ) : (
        <ul className="space-y-2">
          {visits.map((visit) => (
            <VisitRow key={visit.id} visit={visit} />
          ))}
        </ul>
      )}

      {error && visits && (
        <p role="alert" className="mt-3 text-sm text-amber-400">
          Could not load any more just now. Try again when you have signal.
        </p>
      )}
      {hasMore && visits && (
        <button
          type="button"
          onClick={() => void load(page + 1)}
          disabled={busy}
          className="mt-3 flex min-h-11 w-full items-center justify-center rounded-ele border border-ele-field-border font-menu text-xs tracking-wider text-ele-body disabled:opacity-50"
        >
          {busy ? "Loading..." : "Load more"}
        </button>
      )}
    </div>
  );
}
